import {
    addQueryParameter,
    addQueryTimestamp,
    formToQuery,
    objectByString,
    hasHistorySupport
} from '../utilities/utilities';
import ToggleLink from '../toggleLink/toggleLink';
import { loadingStart } from '../loader/loader';
import pubsub from '../pubsub/pubsub';
import axios from 'axios';

/**
 * AjaxForm
 * A plugin that add ajax-request to forms.
 * @version 3.0.0
 * @exports AjaxForm
 */
export default class AjaxForm {
    /**
     * Constructor
     * @param {Object} el - A DOM node.
     * @public
     */
    constructor(el) {
        /** Placeholder for base DOM element. */
        this.el = el;

        /** Placeholder for template (used if this plugin is used with json). */
        this.template = null;

        /** Target DOM id (the list). */
        this.targetId = null;

        /** Placeholder for target DOM element (the list). */
        this.targetEl = null;

        /** Placeholder for array of DOM elements that should be updated on render. */
        this.syncEls = [];

        /** Placeholder for pager DOM element. */
        this.pager = null;

        /** Boolean to indicate if there are any ajax-request in progress. */
        this.requestInProgress = false;

        /** 1 =DOESN'T WORK CORRECTLY RIGHT NOW.  back button navigates between searches. 0 = back button navigates to previous, referring page. */
        this.supportBackButton = 0;

        /** Selector for items. */
        this.itemSelector = 'article';

        /** Boolean indicating if to use cache (experimental). */
        this.useCache = false;

        /** Array which will hold the cache. */
        this.cache = [];

        this._init();
    }

    /**
     * The initialization function for this module.
     * @public
     */
    _init() {
        this.targetId = this.el.getAttribute('aria-controls');
        this.targetEl = document.getElementById(this.targetId);
        this.pager = document.querySelector('[data-type="loadMore"]');

        const useCache = this.el.getAttribute('data-cache');
        if (useCache) {
            this.useCache = useCache.toLowerCase() === 'true';
        }

        if (this.targetEl === null) {
            return false;
        }

        const itemSelector = this.el.getAttribute('data-itemselector');
        if (itemSelector) {
            this.itemSelector = itemSelector;
        }

        const dataSync = this.el.getAttribute('data-sync');
        if (dataSync) {
            this.syncEls = dataSync.split('|');
        }

        this._loadTemplates();
    }

    /**
     * Function that loads all template-files needed by the ajaxForm.
     * @private
     */
    _loadTemplates() {
        const promises = [];

        // Get "base" template for the list
        let tpl = this.el.getAttribute('data-template');
        if (tpl) {
            promises.push(this._loadTemplate(this, tpl));
        }

        // See if we have any other elements that wants to be synced with a template
        for (const id of this.syncEls) {
            const el = document.getElementById(id);
            tpl = el && el.getAttribute('data-sync-template');

            if (tpl) {
                promises.push(this._loadTemplate(el, tpl));
            }
        }

        Promise.all(promises)
            .then(() => this._initEvents());
    }

    /**
     * Function that loads a single template-file for an element.
     * @param {Object} el - A DOM node which use the template.
     * @param {String} template - The template name.
     * @param {Object} callback - An callback function after the template has been loaded.
     * @private
     */
    _loadTemplate(el, template) {
        return import(
			/* webpackChunkName: "template" */ '../../templates/' + template + '.hbs'
        ).then(tpl => {
            el.template = tpl.default;
        });
    }

    /**
     * Function that loads content through ajax.
     * @private
     */
    _load() {
        if (this.requestInProgress) {
            return;
        }

        const method = this.el.getAttribute('method') || 'GET',
            loadingMessage = this.el.getAttribute('data-loading-message') || 'Laddar',
            loadingFilters = document.querySelector('.search-filters'),
            query = formToQuery(this.el),
            isGetMethod = method.toLowerCase() === 'get';

        let url = this.el.getAttribute('action');
        let ajaxUrl = this.el.getAttribute('data-ajaxurl');

        url = addQueryParameter(url, query);

        if (ajaxUrl) {
            ajaxUrl = addQueryParameter(ajaxUrl, query);
            ajaxUrl = addQueryTimestamp(ajaxUrl);
        }

        if (this.useCache === true && this.cache[url] !== undefined) {
            this.targetEl.innerHTML = this.cache[url];
            if (isGetMethod) {
                this._pathchange(url);
            }
            return false;
        }

         if (isGetMethod) {
            this._pathchange(url);
        }

        this.requestInProgress = true;

        loadingStart(this.targetEl, loadingMessage, 'large');

        loadingFilters && loadingFilters.classList.add('search-filters--disabled');

        axios({ method, url: ajaxUrl || url })
            .then(response => {
                this.targetEl.innerHTML = '';

                const ct = (response.headers['content-type'] || '').match(/(html|json)/g)[0];

                // Forskningsportal som filteras utan filters i url:en
                const pagerElement = document.getElementById('search-pager');
                if (pagerElement && pagerElement.parentElement) {
                    pagerElement.parentElement.classList.remove('hide');
                }

                switch (ct) {
                    case 'html':
                        this._renderHtml(response.data, url);
                        break;
                    case 'json':
                        this._renderJson(response.data, url);
                        break;
                }

                pubsub.publish('/ajaxForm/list/updated', { container: this.targetEl, url });
            })
            .catch(() => {
                alert('An error occured, try again later.');
            })
            .then(() => {
                this.requestInProgress = false;
                if (loadingFilters) {
                    loadingFilters.classList.remove('search-filters--disabled');
                }
            });
    }

    /**
     * Function that renders new html-data.
     * @param {String} data - New data as a string of html.
     * @param {String} url - The requested url as a string.
     * @private
     */
    _renderHtml(data) {
        const doc = new DOMParser().parseFromString(data, 'text/html'),
            newPager = doc.querySelector('[data-type="loadMore"]'),
            newItems = doc.querySelectorAll('#' + this.targetId + ' ' + this.itemSelector);

        if (newItems.length) {
            this.targetEl.append(...newItems);
            (newItems[0].querySelector('a') || newItems[0]).focus();
        }

        if (this.pager) {
            if (newPager) {
                this.pager.setAttribute('href', newPager.getAttribute('href'));
                this.pager.classList.remove('btn--hidden');
            } else {
                this.pager.classList.add('btn--hidden');
            }
        }

        if (this.syncEls.length) {
            this._syncElementsHtml(doc);
        }
    }

    /**
     * Function that sync other elements after update (with HTML-data).
     * @param {String} data - New data as a string of json.
     * @private
     */
    _syncElementsHtml(doc) {
        for (const id of this.syncEls) {
            const newEl = doc.getElementById(id);
            if (newEl) {
                document.getElementById(id).innerHTML = newEl.innerHTML;
            }
        }

        pubsub.publish('/ajaxForm/sync/updated');
    }

    /**
     * Function that renders new json-data.
     * @param {String} data - New data as a string of json.
     * @param {String} url - The requested url as a string.
     * @private
     */
    _renderJson(data) {
        let html;

        try {
            html = this.template({ mainConfig, ...data });
        } catch (e) {
            html = '';
        }

        if (this.pager) {
            if (data.pager && data.pager.pagerUrl) {
                this.pager.setAttribute('href', data.pager.pagerUrl);
                this.pager.classList.remove('btn--hidden');
            } else {
                this.pager.classList.add('btn--hidden');
            }
        }

        this.targetEl.insertAdjacentHTML('BeforeEnd', html);

        if (this.syncEls.length) {
            this._syncElementsJson(data);
        }

        if (this.targetEl.querySelector('[data-type="toggle"]')) {
            new ToggleLink(this.targetEl);
        }
    }

    /**
     * Function that sync other elements after update (with JSON-data).
     * @param {String} data - New data as a string of json.
     * @private
     */
    _syncElementsJson(data) {
        for (const id of this.syncEls) {
            const el = document.getElementById(id);

            if (!el) {
                continue;
            }

            const templ = el.getAttribute('data-sync-template');
            const syncWith = el.getAttribute('data-sync-with');
            let html;

            if (templ) {
                const tdata = syncWith ? objectByString(data, syncWith) : data;
                html = el.template(tdata);
            } else if (syncWith) {
                html = objectByString(data, syncWith);
            }

            if (html) {
                el.innerHTML = '';
                el.insertAdjacentHTML('BeforeEnd', html);
            }
        }

        pubsub.publish('/ajaxForm/sync/updated');
    }

    /**
     * Updates the document url
     * @private
     */
    _pathchange(url) {
        if (!hasHistorySupport()) {
            return;
        }
        if (this.supportBackButton === 1) {
            window.history.pushState(null, null, url);
        } else {
            window.history.replaceState(null, null, url);
        }
    }

    /**
     * Initiates events.
     * @private
     */
    _initEvents() {
        this.el.addEventListener('submit', event => {
            event.preventDefault();
            this._load();
        });

        pubsub.subscribe('/ajaxForm/load', () => {
            this._load();
        });

        pubsub.subscribe('/ajaxForm/sync/update/html', doc => {
            setTimeout(() => {
                this._syncElementsHtml(doc);
            }, 500);
        });

        pubsub.subscribe('/ajaxForm/sync/update/json', data => {
            setTimeout(() => {
                this._syncElementsJson(data);
            }, 500);
        });

        pubsub.subscribe('/ajaxForm/list/updated', data => {
            if (this.useCache === true && data.url) {
                this.cache[url] = this.targetEl.innerHTML;
            }
        });
    }
}
